import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { BillableEvent } from './billable-event';
import { Customer } from './customer';
import { EquipmentType } from './damage';
import { Location } from './location';

dayjs.extend(utc);
dayjs.extend(timezone);

const billingBoundary = (value: string, timeZone?: string): Date => {
  if (!timeZone?.trim()) return new Date(value);
  // REST billing treats the supplied wall-clock fields as local to the invoice location.
  // Strip the offset before parsing so neither it nor the browser zone shifts those fields.
  return dayjs.tz(value.replace(/(?:Z|[+-]\d{2}:\d{2})$/i, ''), timeZone).toDate();
};

export interface InvoiceItemInterface {
  id?: number;
  unitId: number;
  unitNumber: string;
  sizeType: string;
  /** ISO date string */
  gateInDate: string;
  /** ISO date string */
  gateOutDate?: string;
  totalDays: number;
  freeDays: number;
  chargedDays: number;
  storageRate: number;
  storageCharge: number;
  gateInCharge: number;
  gateOutCharge: number;
  liftOffCharge: number;
  liftOnCharge: number;
  digCharge: number;
  otherEventCharge: number;
  loaded: boolean;
  setup: boolean;
  unitType: EquipmentType;
  billableEvents: BillableEvent[];
  liftOffCount: number;
  liftOnCount: number;
  digCount: number;
  otherEventCount: number;
}

export class InvoiceItem {
  id: number;
  unitId: number;
  unitNumber: string;
  sizeType: string;
  gateInDate: Date;
  gateOutDate?: Date;
  totalDays: number;
  freeDays: number;
  chargedDays: number;
  storageRate: number;
  storageCharge: number;
  gateInCharge: number;
  gateOutCharge: number;
  liftOffCharge: number;
  liftOnCharge: number;
  digCharge: number;
  otherEventCharge: number;
  loaded: boolean;
  setup: boolean;
  unitType: EquipmentType;
  billableEvents: BillableEvent[];
  liftOffCount: number;
  liftOnCount: number;
  digCount: number;
  otherEventCount: number;
  totalCharge: number;

  constructor(instance: InvoiceItemInterface) {
    this.id = instance.id ?? -1;
    this.unitId = instance.unitId;
    this.unitNumber = instance.unitNumber;
    this.sizeType = instance.sizeType;
    this.gateInDate = new Date(instance.gateInDate);
    this.gateOutDate = !instance.gateOutDate ? undefined : new Date(instance.gateOutDate);
    this.totalDays = instance.totalDays;
    this.freeDays = instance.freeDays;
    this.chargedDays = instance.chargedDays;
    this.storageRate = instance.storageRate;
    this.storageCharge = instance.storageCharge;
    this.gateInCharge = instance.gateInCharge;
    this.gateOutCharge = instance.gateOutCharge;
    this.liftOffCharge = instance.liftOffCharge;
    this.liftOnCharge = instance.liftOnCharge;
    this.digCharge = instance.digCharge;
    this.otherEventCharge = instance.otherEventCharge;
    this.loaded = instance.loaded;
    this.setup = instance.setup;
    this.unitType = instance.unitType;
    this.billableEvents = instance.billableEvents;
    this.liftOffCount = instance.liftOffCount;
    this.liftOnCount = instance.liftOnCount;
    this.digCount = instance.digCount;
    this.otherEventCount = instance.otherEventCount;
    this.totalCharge = this.getAllCharges();
  }

  getAllCharges() {
    return (
      this.storageCharge +
      this.gateInCharge +
      this.gateOutCharge +
      this.liftOnCharge +
      this.liftOffCharge +
      this.digCharge +
      this.otherEventCharge
    );
  }
}

export interface InvoiceInterface {
  id?: number;
  invoiceNumber?: string;
  status: InvoiceStatus;
  customer: Customer;
  /** ISO date string */
  fromDate: string;
  /** ISO date string */
  toDate: string;
  /** ISO date string */
  invoiceDate?: string;
  location: Location;
  items: InvoiceItemInterface[];
  finalized: boolean;
  // snapshot of unit credits
  unitCredits: { [unitId: string]: number };
  statusReason: string;
}

export class Invoice {
  private billingPeriodStart: Date;
  private billingPeriodEnd: Date;
  id: number;
  invoiceNumber?: string;
  status: InvoiceStatus;
  customer: Customer;
  /** ISO date string */
  fromDate: Date;
  /** ISO date Date */
  toDate: Date;
  /** ISO date string */
  invoiceDate?: Date;
  location: Location;
  items: InvoiceItem[];
  finalized: boolean;
  // snapshot of unit credits
  unitCredits: { [unitId: string]: number };
  statusReason: string;

  constructor(instance: InvoiceInterface) {
    this.id = instance.id ?? -1;
    this.invoiceNumber = instance.invoiceNumber;
    this.status = instance.status;
    this.customer = instance.customer;
    this.fromDate = new Date(instance.fromDate);
    this.toDate = new Date(instance.toDate);
    this.invoiceDate = !!instance.invoiceDate ? new Date(instance.invoiceDate) : undefined;
    this.location = instance.location;
    this.billingPeriodStart = billingBoundary(instance.fromDate, instance.location?.timeZone);
    this.billingPeriodEnd = billingBoundary(instance.toDate, instance.location?.timeZone);
    this.items = !!instance.items ? instance.items.map(item => new InvoiceItem(item)) : [];
    this.finalized = instance.finalized;
    this.unitCredits = instance.unitCredits;
    this.statusReason = instance.statusReason;
  }

  isWithinBillingPeriod(movementDate?: Date | null): boolean {
    return !!movementDate && movementDate >= this.billingPeriodStart && movementDate <= this.billingPeriodEnd;
  }

  /** Get a charge from all empty units (genset, chassis, unloaded container + setup) */
  private getEmptyUnitCharge(getCharge: (i: InvoiceItem) => number) {
    return this.items.filter(i => !i.loaded).reduce((sum, i) => (sum += getCharge(i)), 0);
  }

  private getLoadedUnitCharge(getCharge: (i: InvoiceItem) => number) {
    return this.items.filter(i => i.loaded).reduce((sum, i) => (sum += getCharge(i)), 0);
  }

  getTotalCharge() {
    return (
      this.items.reduce((sum, item) => (sum += item.getAllCharges()), 0) -
      Object.values(this.unitCredits).reduce((sum, i) => (sum += i), 0)
    );
  }

  getCredit() {
    return Object.values(this.unitCredits).reduce((sum, i) => (sum += i), 0);
  }

  //#region Empty - All Types
  getEmptyUnitStorageCharges() {
    return this.getEmptyUnitCharge((i: InvoiceItem) => i.storageCharge);
  }

  getEmptyUnitGateInCharges() {
    return this.getEmptyUnitCharge((i: InvoiceItem) => i.gateInCharge);
  }

  getEmptyUnitGateOutCharges() {
    return this.getEmptyUnitCharge((i: InvoiceItem) => i.gateOutCharge);
  }
  //#endregion

  //#region Loaded
  getLoadedUnitStorageCharges() {
    return this.getLoadedUnitCharge((i: InvoiceItem) => i.storageCharge);
  }

  getLoadedUnitGateInCharges() {
    return this.getLoadedUnitCharge((i: InvoiceItem) => i.gateInCharge);
  }

  getLoadedUnitGateOutCharges() {
    return this.getLoadedUnitCharge((i: InvoiceItem) => i.gateOutCharge);
  }
  //#endregion

  // returns a Record mapping sizeType to an object containing the items and the sum of the relevant units' credits
  getItemsGroupedBySizeType() {
    const temp = this.items.reduce<Record<string, { items: InvoiceItem[]; credit: number }>>((prev, curr) => {
      const sizeType = curr.sizeType;
      const group = {
        items: prev[sizeType]?.items || [],
        credit: prev[sizeType]?.credit || 0
      };
      group.items.push(curr);
      group.credit += this.unitCredits[curr.unitId] || 0;
      return { ...prev, [sizeType]: group };
    }, {});
    return temp;
  }

  getUnloadedItemsGroupedByUnitType() {
    return this.items
      .filter(i => !i.loaded)
      .reduce<Record<string, { items: InvoiceItem[]; credit: number }>>((groups, item) => {
        const type = item.setup ? 'SETUP' : item.unitType;
        const group = {
          items: groups[type]?.items || [],
          credit: groups[type]?.credit || 0
        };
        group.items.push(item);
        group.credit += this.unitCredits[item.unitId] || 0;
        return { ...groups, [type]: group };
      }, {});
  }

  getLoadedItemsGroupedByUnitType() {
    return this.items
      .filter(i => i.loaded)
      .reduce<Record<string, { items: InvoiceItem[]; credit: number }>>((groups, item) => {
        const type = item.setup ? 'SETUP' : item.unitType;
        const group = {
          items: groups[type]?.items || [],
          credit: groups[type]?.credit || 0
        };
        group.items.push(item);
        group.credit += this.unitCredits[item.unitId] || 0;
        return { ...groups, [type]: group };
      }, {});
  }
}

export enum InvoiceStatus {
  HOLD = 'HOLD',
  NEEDS_REVIEW = 'NEEDS_REVIEW',
  APPROVED = 'APPROVED'
}

/** Slim customer shape carried by the list summary DTO (`{id, name, code}`). */
export interface InvoiceSummaryCustomer {
  id: number;
  name: string;
  code: string;
}

/**
 * Shape returned by the invoice list endpoints (`GET /invoices`, `GET /invoices/pending`). The
 * backend now sends the aggregate fields directly instead of the full `items` graph. Both the new
 * aggregate fields and the legacy `items`/`unitCredits` are optional so the type tolerates either
 * backend during a coordinated release (see {@link InvoiceSummary}).
 */
export interface InvoiceSummaryInterface {
  id?: number;
  invoiceNumber?: string;
  status: InvoiceStatus;
  statusReason?: string;
  finalized: boolean;
  customer: InvoiceSummaryCustomer | Customer;
  /** ISO date string */
  fromDate: string;
  /** ISO date string */
  toDate: string;
  /** ISO date string */
  invoiceDate?: string;

  // SQL-computed aggregates (present from the summary-DTO backend)
  totalCharge?: number;
  credit?: number;
  chargedDays?: number;
  unitCount?: number;
  emptyStorageCharge?: number;
  loadedStorageCharge?: number;
  emptyGateInCharge?: number;
  loadedGateInCharge?: number;
  emptyGateOutCharge?: number;
  loadedGateOutCharge?: number;

  // legacy entity fields, used only to fall back to client-side math against an older backend
  items?: InvoiceItemInterface[];
  unitCredits?: { [unitId: string]: number };
}

/**
 * A single invoice list row. Reads the aggregate fields the backend now computes in SQL, but falls
 * back to the historical per-item math when the response still carries `items` — so the frontend
 * works whether it is deployed before or after the backend summary-DTO change (release-order
 * independence). The full {@link Invoice} class is still used for the detail page.
 */
export class InvoiceSummary {
  id: number;
  invoiceNumber?: string;
  status: InvoiceStatus;
  statusReason: string;
  finalized: boolean;
  customer: InvoiceSummaryCustomer | Customer;
  fromDate: Date;
  toDate: Date;
  invoiceDate?: Date;

  totalCharge: number;
  credit: number;
  chargedDays: number;
  unitCount: number;
  emptyStorageCharge: number;
  loadedStorageCharge: number;
  emptyGateInCharge: number;
  loadedGateInCharge: number;
  emptyGateOutCharge: number;
  loadedGateOutCharge: number;

  constructor(instance: InvoiceSummaryInterface) {
    this.id = instance.id ?? -1;
    this.invoiceNumber = instance.invoiceNumber;
    this.status = instance.status;
    this.statusReason = instance.statusReason ?? '';
    this.finalized = instance.finalized;
    this.customer = instance.customer;
    this.fromDate = new Date(instance.fromDate);
    this.toDate = new Date(instance.toDate);
    this.invoiceDate = !!instance.invoiceDate ? new Date(instance.invoiceDate) : undefined;

    // When the backend still returns the full graph (pre-summary-DTO), reuse the Invoice class'
    // math; otherwise take the aggregates straight off the DTO. `??` only falls back on
    // null/undefined, so a legitimate 0 aggregate from the DTO is preserved.
    const legacy = instance.items !== undefined ? new Invoice(instance as unknown as InvoiceInterface) : undefined;

    this.totalCharge = instance.totalCharge ?? legacy?.getTotalCharge() ?? 0;
    this.credit = instance.credit ?? legacy?.getCredit() ?? 0;
    this.chargedDays = instance.chargedDays ?? legacy?.items.reduce((sum, item) => sum + item.chargedDays, 0) ?? 0;
    this.unitCount = instance.unitCount ?? legacy?.items.length ?? 0;
    this.emptyStorageCharge = instance.emptyStorageCharge ?? legacy?.getEmptyUnitStorageCharges() ?? 0;
    this.loadedStorageCharge = instance.loadedStorageCharge ?? legacy?.getLoadedUnitStorageCharges() ?? 0;
    this.emptyGateInCharge = instance.emptyGateInCharge ?? legacy?.getEmptyUnitGateInCharges() ?? 0;
    this.loadedGateInCharge = instance.loadedGateInCharge ?? legacy?.getLoadedUnitGateInCharges() ?? 0;
    this.emptyGateOutCharge = instance.emptyGateOutCharge ?? legacy?.getEmptyUnitGateOutCharges() ?? 0;
    this.loadedGateOutCharge = instance.loadedGateOutCharge ?? legacy?.getLoadedUnitGateOutCharges() ?? 0;
  }
}
